import { DEFAULT_WALL_COLOR } from "@gettactic/map-builder/src/components/map/defines";
import type { WallPathShape } from "@gettactic/map-builder/src/types";
import {
  getWallControlPoint,
  quadraticBezierPoint
} from "@gettactic/map-builder/src/utils/bezier";
import { getWallDash } from "@gettactic/map-builder/src/utils/wall";
import React from "react";
import { WallEdge } from "./wallEdge";

export interface WallPathSvgProps {
  shape: WallPathShape;
}

const FILL_SAMPLES = 20;

export const WallPathSvg: React.FC<WallPathSvgProps> = ({ shape }) => {
  const { vertices, edges, closed } = shape;
  const edgeCount = closed ? vertices.length : vertices.length - 1;
  const thickness = shape.thickness;
  const color = shape.color || DEFAULT_WALL_COLOR;
  const dashArray = getWallDash(shape.dashPattern, thickness)?.join(",");

  // Interior fill of a closed path, sampled along curved edges so it
  // follows the wall instead of cutting across the chord.
  const fillPoints = React.useMemo(() => {
    if (!closed || !shape.fillColor || vertices.length < 3) return null;
    const points: string[] = [];
    for (let index = 0; index < vertices.length; index++) {
      const from = vertices[index];
      const to = vertices[(index + 1) % vertices.length];
      const edge = edges[index] || {};
      const curvature = edge.curvature || 0;
      if (curvature === 0) {
        points.push(`${from.x},${from.y}`);
        continue;
      }
      const { cx, cy } = getWallControlPoint(
        from.x,
        from.y,
        to.x,
        to.y,
        curvature,
        edge.curvatureCenter ?? 0.5
      );
      for (let sample = 0; sample < FILL_SAMPLES; sample++) {
        const point = quadraticBezierPoint(
          from.x,
          from.y,
          cx,
          cy,
          to.x,
          to.y,
          sample / FILL_SAMPLES
        );
        points.push(`${point.x},${point.y}`);
      }
    }
    return points.join(" ");
  }, [closed, shape.fillColor, vertices, edges]);

  return (
    <g>
      {fillPoints ? (
        <polygon
          points={fillPoints}
          fill={shape.fillColor}
          opacity={shape.fillOpacity ?? 1}
        />
      ) : null}
      {Array.from({ length: Math.max(0, edgeCount) }).map((_, edgeIndex) => {
        const from = vertices[edgeIndex];
        const to = vertices[(edgeIndex + 1) % vertices.length];
        const edge = edges[edgeIndex] || {};
        const length = Math.hypot(to.x - from.x, to.y - from.y);
        const fromHasTwoEdges = closed || edgeIndex > 0;
        const toHasTwoEdges = closed || edgeIndex + 1 < vertices.length - 1;
        // Overlap the corner arc by 1px so the butt cap hides behind it.
        const startOffset =
          fromHasTwoEdges && length > 0
            ? Math.max(0, Math.min(from.cornerRadius || 0, length / 2) - 1)
            : 0;
        const endOffset =
          toHasTwoEdges && length > 0
            ? Math.max(0, Math.min(to.cornerRadius || 0, length / 2) - 1)
            : 0;
        return (
          <WallEdge
            // biome-ignore lint/suspicious/noArrayIndexKey: edges are positional by definition
            key={edgeIndex}
            from={from}
            to={to}
            thickness={thickness}
            color={color}
            dashArray={dashArray}
            curvature={edge.curvature || 0}
            curvatureCenter={edge.curvatureCenter ?? 0.5}
            doors={edge.doors}
            windows={edge.windows}
            startOffset={startOffset}
            endOffset={endOffset}
          />
        );
      })}
      {!closed &&
        (shape.lineCapStart || "butt") === "round" &&
        vertices.length > 0 && (
          <circle
            cx={vertices[0].x}
            cy={vertices[0].y}
            r={thickness / 2}
            fill={color}
          />
        )}
      {!closed &&
        (shape.lineCapEnd || "butt") === "round" &&
        vertices.length > 1 && (
          <circle
            cx={vertices[vertices.length - 1].x}
            cy={vertices[vertices.length - 1].y}
            r={thickness / 2}
            fill={color}
          />
        )}
      {vertices.map((vertex, vertexIndex) => {
        const cornerRadius = vertex.cornerRadius;
        if (!cornerRadius) return null;
        const hasPrevious = closed || vertexIndex > 0;
        const hasNext = closed || vertexIndex < vertices.length - 1;
        if (!hasPrevious || !hasNext) return null;
        const previous =
          vertices[(vertexIndex - 1 + vertices.length) % vertices.length];
        const next = vertices[(vertexIndex + 1) % vertices.length];
        const toPrevious = {
          x: previous.x - vertex.x,
          y: previous.y - vertex.y
        };
        const toNext = { x: next.x - vertex.x, y: next.y - vertex.y };
        const previousLength = Math.hypot(toPrevious.x, toPrevious.y);
        const nextLength = Math.hypot(toNext.x, toNext.y);
        if (previousLength === 0 || nextLength === 0) return null;
        const radius = Math.min(
          cornerRadius,
          previousLength / 2,
          nextLength / 2
        );
        const p1 = {
          x: vertex.x + (toPrevious.x / previousLength) * radius,
          y: vertex.y + (toPrevious.y / previousLength) * radius
        };
        const p2 = {
          x: vertex.x + (toNext.x / nextLength) * radius,
          y: vertex.y + (toNext.y / nextLength) * radius
        };
        return (
          <path
            // biome-ignore lint/suspicious/noArrayIndexKey: vertices are positional by definition
            key={vertexIndex}
            d={`M ${p1.x},${p1.y} Q ${vertex.x},${vertex.y} ${p2.x},${p2.y}`}
            stroke={color}
            strokeWidth={thickness}
            strokeLinecap="round"
            fill="none"
          />
        );
      })}
    </g>
  );
};
